import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import moment from 'moment';
import {FiMail, FiX, FiSend, FiCheckCircle} from 'react-icons/fi';
import './ConsultationReplyDialog.css';

const orderMails = mails => [...mails].sort((a,b) => (Date.parse(b.date)||0)-(Date.parse(a.date)||0) || Number(b.uid)-Number(a.uid));
const storageRead = key => {try{return JSON.parse(sessionStorage.getItem(key));}catch{return null;}};
const storageWrite = (key,value) => {try{sessionStorage.setItem(key,JSON.stringify(value));}catch{}};
const storageRemove = key => {try{sessionStorage.removeItem(key);}catch{}};
const states = {queued:'Antwort wird versendet …',sending:'Versand läuft …',sent:'Antwort versendet',failed:'Antwort vom Mailserver abgelehnt',uncertain:'Versandstatus unklar – bitte Postfach prüfen.'};

export default function ConsultationReplyDialog({item,onClose}) {
  const storageKey=`prime-ink-reply-${item.id}`;
  const restored=useRef(storageRead(storageKey));
  const draft=restored.current;
  const firstName=String(item.clientName || '').trim().split(/\s+/)[0] || '';
  const [mails,setMails]=useState([]);
  const [original,setOriginal]=useState(draft?.originalMail || null);
  const [subject,setSubject]=useState(draft?.subject || '');
  const [body,setBody]=useState(draft?.body || `Hey${firstName ? ' '+firstName : ''},\n\n\n\nLiebe Grüße\nPrime Ink Team`);
  const [result,setResult]=useState(null);
  const [requestKey,setRequestKey]=useState(draft?.requestKey || crypto.randomUUID());
  const [loading,setLoading]=useState(true);
  const [sending,setSending]=useState(false);
  const [error,setError]=useState('');
  const [notice,setNotice]=useState('');
  const [statusReady,setStatusReady]=useState(!draft);
  const inFlight=useRef(false);
  const dialogRef=useRef(null);
  const mounted=useRef(true);
  const subjectEdited=useRef(Boolean(draft));
  const blocked=Boolean(result && result.state!=='none');

  useEffect(()=>{
    mounted.current=true;
    const controller=new AbortController();
    axios.get(`/api/consultations/${item.id}/mail-candidates`,{signal:controller.signal})
      .then(res=>{
        if(controller.signal.aborted)return;
        const list=orderMails(Array.isArray(res.data)?res.data:[]);
        const direct=list.filter(m=>String(m.from || '').split(',').some(address=>address.trim().toLowerCase()===String(item.email || '').trim().toLowerCase()));
        const sorted=direct.length ? direct : list;
        setMails(sorted);
        if(!draft){
          setOriginal(sorted[0] || null);
          if(!subjectEdited.current)setSubject(replySubject(sorted[0]?.subject || item.subject || 'Deine Tattoo-Anfrage'));
        }
        setNotice(sorted.length ? 'Neueste Kundenmail vorausgewählt. Ältere Mails kannst Du auswählen.' : 'Keine passende Kundenmail gefunden. Antworten ist erst mit einer zugehörigen Mail möglich.');
      })
      .catch(e=>{if(!controller.signal.aborted)setNotice(e.response?.data?.message || 'Postfach derzeit nicht erreichbar. Bitte den Dialog erneut öffnen.');})
      .finally(()=>{if(!controller.signal.aborted)setLoading(false);});
    return ()=>{mounted.current=false;controller.abort();};
    // The parent mounts a fresh dialog for each consultation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[item.id]);

  useEffect(()=>{
    const previous=document.activeElement;
    dialogRef.current?.querySelector('button')?.focus();
    return ()=>previous?.focus?.();
  },[]);
  function handleKeys(event){
    if(event.key==='Escape' && !inFlight.current){event.stopPropagation();onClose();}
    if(event.key!=='Tab')return;
    const elements=[...dialogRef.current.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled)')].filter(e=>!e.closest('fieldset:disabled'));
    const first=elements[0],last=elements[elements.length-1];
    if(event.shiftKey && document.activeElement===first){event.preventDefault();last?.focus();}
    else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first?.focus();}
  }
  async function checkStatus(){
    try{
      const res=await axios.get(`/api/consultations/${item.id}/reply-status/${requestKey}`);
      if(!mounted.current)return;
      setResult(res.data.state==='none'?null:res.data);setStatusReady(true);setError('');
    }catch(e){if(mounted.current){setStatusReady(false);setError(e.response?.data?.message || 'Versandstatus derzeit nicht erreichbar. Bitte Status prüfen.');}}
  }
  useEffect(()=>{
    if(!draft)return;
    void checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[requestKey]);
  useEffect(()=>{
    if(!result || !['queued','sending','sent'].includes(result.state) || (result.state==='sent' && result.sentCopyDone && result.inboxReadDone))return;
    const timer=setInterval(()=>void checkStatus(),4000);
    return ()=>clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[result?.state,result?.sentCopyDone,result?.inboxReadDone,requestKey]);
  function replySubject(value){return /^re\s*:/i.test(value) ? value : `Re: ${value}`;}
  function selectMail(uid){
    const next=mails.find(m=>String(m.uid)===uid) || null;setOriginal(next);
    if(!subjectEdited.current)setSubject(replySubject(next?.subject || item.subject || 'Deine Tattoo-Anfrage'));
  }
  async function send(){
    if(inFlight.current || blocked || !statusReady || !original || !subject.trim() || !body.trim())return;
    inFlight.current=true;setSending(true);setError('');
    const payload={subject,body,originalMail:original,requestKey};
    storageWrite(storageKey,payload);
    try{
      const res=await axios.post(`/api/consultations/${item.id}/reply`,payload);
      if(mounted.current)setResult(res.data);
    }catch(e){
      if(mounted.current){setStatusReady(false);setError(e.response?.data?.message || 'Keine Versandbestätigung erhalten. Bitte Status prüfen.');}
    }finally{inFlight.current=false;if(mounted.current)setSending(false);}
  }
  function newReply(){
    storageRemove(storageKey);restored.current=null;setResult(null);setRequestKey(crypto.randomUUID());setStatusReady(true);setError('');
    setBody(`Hey${firstName ? ' '+firstName : ''},\n\n\n\nLiebe Grüße\nPrime Ink Team`);
  }
  const canSend=!loading && !sending && !blocked && statusReady && original && subject.trim() && body.trim();
  return <div className="pi-reply-overlay" onClick={()=>{if(!sending)onClose();}}>
    <section ref={dialogRef} onKeyDown={handleKeys} className="pi-reply-dialog" role="dialog" aria-modal="true" aria-labelledby="pi-reply-title" onClick={e=>e.stopPropagation()}>
      <header><div><span className="pi-reply-eyebrow"><FiMail/> Antwort auf Kundenanfrage</span><h3 id="pi-reply-title">{item.clientName || 'Kundenmail beantworten'}</h3></div>
        <button type="button" className="pi-reply-close" aria-label="Schließen" disabled={sending} onClick={onClose}><FiX/></button></header>
      <p className="pi-reply-recipient">An: {item.email}</p>
      <fieldset disabled={sending || blocked || !statusReady}>
        <label>Kundenmail<select value={original?.uid || ''} onChange={e=>selectMail(e.target.value)} disabled={loading || sending || blocked || !statusReady}>
          <option value="">Kundenmail auswählen</option>
          {mails.map(m=><option key={m.uid} value={m.uid}>{m.date?moment(m.date).format('DD.MM.YYYY HH:mm'):''} · {m.subject} · {m.from}</option>)}
        </select></label>
        <p className="pi-reply-help">{loading?'Kundenmails werden geladen …':notice}</p>
        <label>Betreff<input value={subject} maxLength={200} onChange={e=>{subjectEdited.current=true;setSubject(e.target.value);}}/></label>
        <label>Deine Antwort<textarea value={body} rows={9} maxLength={10000} onChange={e=>setBody(e.target.value)}/></label>
      </fieldset>
      {result && <div className={`pi-reply-status ${result.state==='sent'?'success':''}`} role="status">
        <strong>{result.state==='sent' && <FiCheckCircle/>}{states[result.state] || result.state}</strong>
        {result.state==='sent' && <p>{result.sentCopyDone?'Kopie unter „Gesendet“ gespeichert.':'Kopie unter „Gesendet“ wird gespeichert.'} {result.inboxReadDone?'Kundenmail als gelesen markiert.':''}</p>}
        {result.syncError && <p>{result.syncError}</p>}
      </div>}
      {error && <p className="pi-reply-error" role="alert">{error}</p>}
      <footer>
        <button type="button" disabled={sending} onClick={onClose}>Schließen</button>
        {(!statusReady || result?.state==='uncertain') && <button type="button" disabled={sending} onClick={checkStatus}>Status prüfen</button>}
        {result && ['sent','failed'].includes(result.state) ? <button type="button" onClick={newReply}>Weitere Antwort schreiben</button> : <button type="button" className="primary" disabled={!canSend} onClick={send}><FiSend/> {sending?'Wird gespeichert …':'Antwort senden'}</button>}
      </footer>
    </section>
  </div>;
}
